/**
 * DocImportReview — Document → Stories (See the work).
 *
 * Upload a document (.docx / .txt / .md) → Avium extracts the action items
 * (Avium Intelligence on Team+, deterministic bullet parser otherwise) → the
 * user REVIEWS the recommendations (each card says exactly which ticket field
 * its content lands in: Summary = the title, Description = the structured
 * template: Due dates · Requirements · Action items · Milestones · Notes) →
 * commit creates Avium tickets (issueType Story).
 *
 * Trust contract mirrored from the server: nothing is created until the user
 * explicitly commits — extraction is a recommendation, the human approves it.
 *
 * Brand chrome: every stage opens with the product's dark-teal band + amber
 * diamond eyebrow (the PageChrome banner language), so the wizard reads as
 * Avium even when hosted in the global modal over another page.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { docImportApi, projectsApi } from '../../lib/api';
import type { Project } from '../../types';
import { isOwnedProjectSource, PROJECT_SOURCE_LABEL } from '../../types';
import {
  DOC_IMPORT_EXTENSIONS, TICKETS_CREATED_EVENT, isDocImportFile,
  DOC_TEMPLATE_SECTIONS, docTemplateSection, docSectionLines,
} from '../../lib/addDataConfig';
import { WizardBand, WizardSteps, type WizardStepDef } from '../shared/WizardChrome';
import RichTextEditor from '../shared/RichTextEditor';
import MarkdownView from '../shared/MarkdownView';

interface DraftSection {
  heading: string;
  /** Markdown — one entry per paragraph/line; inline marks (bold/italic/…) allowed.
      Converted back to the server's lines[] shape at commit. */
  md: string;
}

interface DraftItem {
  title: string;
  sections: DraftSection[];
  included: boolean;
}

type Stage = 'upload' | 'analyzing' | 'review' | 'committing' | 'done';

interface DocImportReviewProps {
  /** Pre-loaded file (global drag-and-drop) — analyzed immediately on mount. */
  initialFile?: File | null;
  /** Provided when hosted in the global modal — lets "Open the board" close it. */
  onRequestClose?: () => void;
}

/** All bands in this wizard share the feature eyebrow. */
const Band = (props: Omit<Parameters<typeof WizardBand>[0], 'eyebrow'>) =>
  <WizardBand eyebrow="Document → Stories" {...props} />;

/** The wizard's workflow — shown on every stage so you always know where you are. */
type DocStage = 'upload' | 'read' | 'approve';
const DOC_STEPS: Array<WizardStepDef<DocStage>> = [
  { id: 'upload',  label: 'Upload',        hint: '.docx · .txt · .md' },
  { id: 'read',    label: 'Avium reads it', hint: 'Finds the action items & detail' },
  { id: 'approve', label: 'You approve',   hint: 'Nothing is created until then' },
];

export default function DocImportReview({ initialFile, onRequestClose }: DocImportReviewProps = {}) {
  const [stage, setStage] = useState<Stage>('upload');
  const [fileName, setFileName] = useState('');
  const [source, setSource] = useState<'ai' | 'heuristic'>('ai');
  const [truncated, setTruncated] = useState(false);
  const [items, setItems] = useState<DraftItem[]>([]);
  const [created, setCreated] = useState<Array<{ id: string; key: string; summary: string }>>([]);
  const [dragging, setDragging] = useState(false);
  // Optional project to file the new Stories under (Avium-native only).
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectId, setProjectId] = useState('');
  useEffect(() => { projectsApi.list().then((r: { data: Project[] }) => setProjects(r.data.filter(p => isOwnedProjectSource(p.source)))).catch(() => {}); }, []);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const analyze = async (file: File) => {
    if (!isDocImportFile(file.name)) {
      toast.error('Upload a Word (.docx), text (.txt), or Markdown (.md) document');
      return;
    }
    setStage('analyzing');
    try {
      const res = await docImportApi.analyze(file);
      const found = (res.data.items ?? []) as Array<{
        title: string;
        sections?: Array<{ heading: string; lines: string[] }>;
        requirements?: string[]; // legacy server shape — folds into Requirements
      }>;
      setFileName(res.data.fileName ?? file.name);
      setSource(res.data.source === 'heuristic' ? 'heuristic' : 'ai');
      setTruncated(!!res.data.truncated);
      setItems(found.map(i => ({
        title: i.title,
        sections: (i.sections ?? (i.requirements?.length ? [{ heading: 'Requirements', lines: i.requirements }] : []))
          .map(s => ({ heading: s.heading, md: (s.lines ?? []).join('\n\n') })),
        included: true,
      })));
      setStage('review');
      if (!found.length) toast('No action items found — you can still add tickets manually on the board.', { icon: 'ℹ️' });
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: string } } }).response?.data?.error;
      toast.error(msg || 'Could not read that document');
      setStage('upload');
    }
  };

  // Global intake (drag-and-drop / "+ Add data") hands the file in — start
  // reading it immediately so the modal opens already working, not on a blank
  // upload screen the user has to re-drop onto.
  useEffect(() => {
    if (initialFile) void analyze(initialFile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFile]);

  const commit = async () => {
    const chosen = items.filter(i => i.included && i.title.trim());
    if (!chosen.length) { toast.error('Select at least one recommendation to import'); return; }
    setStage('committing');
    try {
      const res = await docImportApi.commit({
        fileName,
        source,
        projectId: projectId || undefined,
        items: chosen.map(i => ({
          title: i.title.trim(),
          sections: i.sections
            // Each paragraph/line is one entry; docSectionLines strips the
            // editor's markdown markers (lists, checkboxes, headings) — the
            // section IS the list, buildDescription adds bullets server-side.
            .map(s => ({ heading: s.heading, lines: docSectionLines(s.md) }))
            .filter(s => s.lines.length > 0),
        })),
      });
      setCreated(res.data.tickets ?? []);
      setStage('done');
      toast.success(`${res.data.created} ${res.data.created === 1 ? 'Story' : 'Stories'} created`);
      // Mounted boards/dashboards refetch so the new Stories appear without a
      // page refresh (mirrors avium:demo-data-loaded / status-mapping-changed).
      window.dispatchEvent(new CustomEvent(TICKETS_CREATED_EVENT));
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: string } } }).response?.data?.error;
      toast.error(msg || 'Import failed');
      setStage('review');
    }
  };

  const setItem = (idx: number, patch: Partial<DraftItem>) =>
    setItems(prev => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const setSection = (idx: number, heading: string, md: string) =>
    setItems(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      const sections = it.sections.some(s => s.heading === heading)
        ? it.sections.map(s => (s.heading === heading ? { ...s, md } : s))
        : [...it.sections, { heading, md }];
      return { ...it, sections };
    }));

  const addSection = (idx: number, heading: string) => setSection(idx, heading, '');

  // ── Upload ──────────────────────────────────────────────────────────────────
  if (stage === 'upload' || stage === 'analyzing') {
    const busy = stage === 'analyzing';
    return (
      <div className="max-w-2xl">
        <Band
          title={busy ? 'Reading your document…' : 'Turn meeting notes into Stories'}
          sub="Avium reads the document, finds the action items, and drafts one Story per item — with the analysis itemized into a structured template. Nothing is created until you review and approve."
        />

        {/* The workflow — upload → Avium reads → you approve */}
        <WizardSteps current={busy ? 'read' : 'upload'} steps={DOC_STEPS} />

        <div
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files[0]; if (f && !busy) analyze(f); }}
          onClick={() => !busy && inputRef.current?.click()}
          className={`border-2 border-dashed rounded-2xl p-14 text-center transition-all bg-gradient-to-b from-white to-brand-accent-light/40 ${
            busy     ? 'border-line cursor-wait' :
            dragging ? 'border-brand-accent bg-brand-primary-light dark:bg-brand-primary/25 cursor-copy shadow-lg' :
                       'border-teal-200 hover:border-brand-accent hover:shadow-lg cursor-pointer'
          }`}
        >
          <div className="text-5xl mb-3">{busy ? '✨' : '📝'}</div>
          <div className="font-bold text-ink text-xl">
            {busy ? 'Extracting the work…' : 'Drop your document here'}
          </div>
          <div className="text-sm text-ink-faint mt-1 font-medium">
            {busy ? 'Due dates · Requirements · Action items · Milestones' : 'or click to browse'}
          </div>
          <div className="text-xs text-ink-faint mt-4">Supported: {DOC_IMPORT_EXTENSIONS.map(e => `.${e}`).join(' · ')} &nbsp;·&nbsp; up to 20 MB</div>
          <input
            ref={inputRef} type="file" accept={DOC_IMPORT_EXTENSIONS.map(e => `.${e}`).join(',')} className="hidden"
            onChange={e => { const f = e.target.files?.[0]; if (f) analyze(f); e.target.value = ''; }}
          />
        </div>
      </div>
    );
  }

  // ── Done ────────────────────────────────────────────────────────────────────
  if (stage === 'done') {
    return (
      <div className="max-w-2xl">
        <Band
          title="Stories created ✓"
          sub={`${created.length} ${created.length === 1 ? 'Story' : 'Stories'} from "${fileName}" are on the board — unassigned, ready for sprint, assignee, and estimates whenever you are.`}
        />
        <WizardSteps current={'approve' as DocStage} steps={DOC_STEPS} completed />
        <div className="space-y-2 mb-6">
          {created.map(t => (
            <div key={t.id} className="flex items-center gap-3 p-3 bg-card border border-line rounded-lg shadow-sm">
              <span className="text-xs font-bold text-brand-primary dark:text-brand-accent bg-brand-primary-light dark:bg-brand-primary/25 rounded px-2 py-1">{t.key}</span>
              <span className="text-sm font-medium text-ink truncate">{t.summary}</span>
              <span className="ml-auto text-xs text-ink-faint">Story</span>
            </div>
          ))}
        </div>
        <div className="flex gap-3">
          {onRequestClose ? (
            <button
              onClick={() => { onRequestClose(); navigate('/pipeline'); }}
              className="bg-brand-primary hover:bg-brand-primary-dark text-white font-semibold px-5 py-2.5 rounded-lg text-sm transition-colors"
            >
              Open the board →
            </button>
          ) : (
            <Link to="/pipeline" className="bg-brand-primary hover:bg-brand-primary-dark text-white font-semibold px-5 py-2.5 rounded-lg text-sm transition-colors">
              Open the board →
            </Link>
          )}
          <button
            onClick={() => { setStage('upload'); setItems([]); setCreated([]); }}
            className="border border-line text-ink-muted hover:bg-wash font-semibold px-5 py-2.5 rounded-lg text-sm transition-colors"
          >
            Import another document
          </button>
        </div>
      </div>
    );
  }

  // ── Review ──────────────────────────────────────────────────────────────────
  const committing = stage === 'committing';
  const chosenCount = items.filter(i => i.included && i.title.trim()).length;
  return (
    <div className="max-w-3xl">
      <Band
        title="Review before anything is created"
        sub={`Avium read "${fileName}" and drafted ${items.length} Story ${items.length === 1 ? 'recommendation' : 'recommendations'}. Edit anything, untick what shouldn't become work — nothing lands on the board until you approve.`}
        right={
          <span className={`text-[11px] font-bold uppercase tracking-wider rounded-full px-3 py-1 whitespace-nowrap ${
            source === 'ai' ? 'bg-amber-300/20 text-amber-300 border border-amber-300/40' : 'bg-card/10 text-teal-100 border border-white/20'
          }`}>
            {source === 'ai' ? '✨ Avium Intelligence' : 'Bullet parser'}
          </span>
        }
      />
      <WizardSteps current={'approve' as DocStage} steps={DOC_STEPS} />
      {truncated && (
        <p className="text-xs text-orange-600 font-medium mb-3">
          Long document — only the first part was read. Split very large docs for full coverage.
        </p>
      )}

      <div className="space-y-4 mb-5">
        {items.map((it, idx) => {
          const missingTemplate = DOC_TEMPLATE_SECTIONS.filter(t => !it.sections.some(s => s.heading.toLowerCase() === t.heading.toLowerCase()));
          return (
            <div key={idx} className={`border rounded-2xl bg-card overflow-hidden shadow-sm transition-opacity ${it.included ? 'border-line' : 'border-line-soft opacity-50'}`}>
              {/* Card head — this is a SUGGESTION, numbered */}
              <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-brand-primary-light/50 to-white border-b border-line-soft">
                <input
                  type="checkbox" checked={it.included}
                  onChange={e => setItem(idx, { included: e.target.checked })}
                  className="w-4 h-4 accent-[#0F766E]"
                  title={it.included ? 'Included — will become a Story' : 'Excluded — will be skipped'}
                />
                <span className="text-[11px] font-black uppercase tracking-wider text-brand-spark-dark bg-brand-spark-light dark:bg-brand-spark/10 border border-orange-200 rounded-full px-2.5 py-0.5">
                  ◆ Recommendation #{idx + 1}
                </span>
                <span className="ml-auto text-[11px] font-bold uppercase tracking-wider text-brand-primary dark:text-brand-accent bg-brand-primary-light dark:bg-brand-primary/25 rounded-full px-2.5 py-0.5">Story</span>
              </div>

              <div className="p-4">
                {/* Summary — explicit about the destination field */}
                <label className="block text-[10px] font-black tracking-normal text-ink-faint mb-1">
                  Summary<span className="font-semibold normal-case tracking-normal text-ink-faint">· becomes the ticket title</span>
                </label>
                <input
                  value={it.title}
                  onChange={e => setItem(idx, { title: e.target.value })}
                  placeholder="Brief title (the ticket summary)"
                  className="w-full font-semibold text-ink text-sm border border-line hover:border-line focus:border-brand-primary rounded-md px-3 py-2 outline-none"
                />

                {/* Description — the structured template */}
                <div className="flex items-baseline justify-between mt-4 mb-1.5">
                  <label className="block text-[10px] font-black tracking-normal text-ink-faint">
                    Description<span className="font-semibold normal-case tracking-normal text-ink-faint">· lands in the ticket as the template below</span>
                  </label>
                </div>
                <div className="rounded-xl border border-line divide-y divide-line-soft">
                  {it.sections.map(s => {
                    const meta = docTemplateSection(s.heading);
                    return (
                      <div key={s.heading} className="px-3 py-2.5">
                        <div className="text-xs font-bold text-brand-primary-dark dark:text-brand-primary-lighter flex items-center gap-1.5 mb-1">
                          <span>{meta?.icon ?? '📌'}</span> {s.heading}
                          {meta?.checklist && <span className="text-[9px] font-bold tracking-normal text-ink-faint ml-1">checklist</span>}
                        </div>
                        {it.included ? (
                          /* Full rich-text toolbar (headings · B/I/U · bullet/
                             numbered/checklist · link · image) — the same editor
                             as the workboard's Create-ticket Description, kept
                             compact per section (one entry per line); excluded
                             items render read-only to keep the screen light. */
                          <RichTextEditor
                            mode="full"
                            bodyMinHeightClass="min-h-[44px]"
                            value={s.md}
                            onChange={md => setSection(idx, s.heading, md)}
                            placeholder={meta?.hint ?? 'One entry per line'}
                          />
                        ) : (
                          <MarkdownView markdown={s.md} className="px-2 py-1.5" />
                        )}
                      </div>
                    );
                  })}
                  {it.sections.length === 0 && (
                    <div className="px-3 py-3 text-xs text-ink-faint">No detail extracted — add a template section below, or the description will note "see source document".</div>
                  )}
                  {missingTemplate.length > 0 && (
                    <div className="px-3 py-2.5 flex flex-wrap items-center gap-1.5 bg-wash/60">
                      <span className="text-[10px] font-bold tracking-normal text-ink-faint mr-1">Add section:</span>
                      {missingTemplate.map(t => (
                        <button
                          key={t.heading}
                          onClick={() => addSection(idx, t.heading)}
                          className="text-xs font-semibold text-brand-primary dark:text-brand-accent border border-teal-200 hover:bg-brand-primary-light dark:hover:bg-brand-primary/25 rounded-full px-2.5 py-1 transition-colors"
                        >
                          + {t.icon} {t.heading}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {projects.length > 0 && (
        <div className="flex items-center gap-2 mb-3">
          <label className="text-sm font-semibold text-ink-muted">Project</label>
          <select
            value={projectId}
            onChange={e => setProjectId(e.target.value)}
            className="input text-sm py-1.5 w-64"
            disabled={committing}
          >
            <option value="">No project</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.key} — {p.name} · {PROJECT_SOURCE_LABEL[p.source]}</option>)}
          </select>
          <span className="text-xs text-ink-faint">Optional — file these Stories under a project.</span>
        </div>
      )}

      <div className="flex items-center gap-3">
        <button
          onClick={commit}
          disabled={committing || chosenCount === 0}
          className="bg-gradient-to-r from-brand-primary to-brand-accent-dark hover:from-brand-primary-dark hover:to-brand-primary disabled:opacity-50 text-white font-bold px-6 py-2.5 rounded-lg text-sm transition-all shadow-md hover:shadow-lg"
        >
          {committing ? 'Creating…' : `Approve ${chosenCount} of ${items.length} → Create ${chosenCount === 1 ? 'Story' : 'Stories'}`}
        </button>
        <button
          onClick={() => { setStage('upload'); setItems([]); }}
          disabled={committing}
          className="border border-line text-ink-muted hover:bg-wash font-semibold px-5 py-2.5 rounded-lg text-sm transition-colors"
        >
          Start over
        </button>
        <span className="text-xs text-ink-faint ml-auto">Tickets land unassigned — add sprint, assignee, and estimates on the board.</span>
      </div>
    </div>
  );
}
